import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import bitmapFonts from 'src/lib/bitmapFonts';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import { parseAmount } from 'src/replicant/utils/beautifyNumber';
import { ProductId } from 'src/replicant/iap';
import { shopBundlesGetGemsReward } from 'src/replicant/match2/shopbundles/match2-shopbundles.getters';
import { shopBundlesGetProductPriceStr } from './match2-shopbundles.utils';

export type Match2ShopBundlesGemsParams = {
  productId: ProductId;
  onPurchase: (productId: ProductId) => void;
};

export class Match2ShopBundlesGems extends View {
  public readonly bg: ImageScaleView;
  public readonly base: ImageScaleView;
  public readonly icon: ImageScaleView;
  public readonly gems: LangBitmapFontTextView;
  public purchaseButton: ButtonScaleViewWithText;
  public productId!: ProductId;
  public onPurchase!: (productId: ProductId) => void;

  constructor(opts: ViewOpts) {
    super({
      width: 670,
      height: 97,
      ...opts,
    });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/match2/shopbundles/shopbundles_gems_bg.png',
      imgScale: 0.66,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 45, right: 45 },
      },
    });

    this.base = new ImageScaleView({
      superview: this,
      width: 167,
      height: 47,
      x: 50,
      y: 20,
      image: 'assets/ui/match2/shopbundles/shopbundles_gems_base.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 20, right: 20 },
      },
    });

    this.icon = new ImageScaleView({
      superview: this,
      width: 60,
      height: 60,
      x: 30,
      y: 12,
      image: 'assets/ui/shared/icons/gem.png',
      scaleMethod: 'contain',
    });

    this.gems = new LangBitmapFontTextView({
      superview: this,
      x: 50,
      y: 10,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Title',
      width: 200,
      height: 66,
    });

    this.purchaseButton = new ButtonScaleViewWithText({
      superview: this,
      tag: 'exchangeBtn',
      ...uiConfig.buttons.primary,
      image: 'assets/ui/match2/shopbundles/shopbundles_button.png',
      textColor: '#ffffff',
      fontSize: 30,
      labelOffsetX: -3,
      labelOffsetY: -1,
      font: bitmapFonts('Body'),
      x: 420,
      y: 10,
      width: 221,
      height: 66,
      pressedOffsetY: 2,
      localeText: () => i18n('currencyExchange.gemsShop.exchangeButton'),
      onClick: async () => this.onPurchase(this.productId),
      tracking: { feature: 'gems-shop', $subFeature: 'gems-shop-exchange' },
    });
  }

  public setup(params: Match2ShopBundlesGemsParams) {
    this.productId = params.productId;
    this.onPurchase = params.onPurchase;
    this.updatePriceText();
    this.updateGemsText();
  }

  private updateGemsText() {
    const gemsReward = shopBundlesGetGemsReward(this.productId);
    const gems = parseAmount(gemsReward);
    const text = `x${gems}`;
    this.gems.localeText = () => text;
  }

  private updatePriceText() {
    const price = shopBundlesGetProductPriceStr(this.productId);
    if (price) {
      this.purchaseButton.show();
      this.purchaseButton.localeText = () => price;
    } else {
      // If price is not defined, the product is not present in the catalog
      // Instead of throwing an error, it is being reported to Sentry, so the Ui does not break
      this.purchaseButton.hide();
    }
  }
}
